import { apiService, ApiResponse } from './api';
import { 
  Vehicle, 
  VehicleFilters, 
  CreateVehicleDto, 
  UpdateVehicleStatusDto, 
  ScheduleMaintenanceDto,
  FleetSummary,
  VehicleAvailability
} from '../types/vehicle';

export class VehicleService {
  private basePath = '/api/cars';

  async getAllVehicles(filters?: VehicleFilters): Promise<ApiResponse<Vehicle[]>> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    return apiService.get<Vehicle[]>(endpoint);
  }

  async getVehicleById(id: string): Promise<ApiResponse<Vehicle>> {
    return apiService.get<Vehicle>(`${this.basePath}/${id}`);
  }

  async createVehicle(vehicleData: CreateVehicleDto): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(`${this.basePath}/admin`, vehicleData);
  }

  async updateVehicle(id: string, vehicleData: Partial<CreateVehicleDto>): Promise<ApiResponse<Vehicle>> {
    return apiService.put<Vehicle>(`${this.basePath}/admin/${id}`, vehicleData);
  }

  async updateVehicleStatus(id: string, statusData: UpdateVehicleStatusDto): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(`${this.basePath}/${id}/status`, statusData);
  }

  async scheduleMaintenance(id: string, maintenanceData: ScheduleMaintenanceDto): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(`${this.basePath}/${id}/maintenance`, maintenanceData);
  }

  async completeMaintenance(
    id: string, 
    maintenanceIndex: number, 
    completionData: {
      completedDate: string;
      actualCost?: number;
      notes?: string;
      performedBy?: string;
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${id}/maintenance/${maintenanceIndex}/complete`, 
      completionData
    );
  }

  async reportIssue(
    id: string, 
    issueData: {
      description: string;
      severity?: 'low' | 'medium' | 'high' | 'critical';
      reportedBy?: string;
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(`${this.basePath}/${id}/issues`, issueData);
  }

  async resolveIssue(
    id: string, 
    issueIndex: number, 
    resolutionData: {
      resolution: string;
      resolvedBy?: string;
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${id}/issues/${issueIndex}/resolve`, 
      resolutionData
    );
  }

  async addVehicleImage(
    id: string, 
    imageData: {
      url: string;
      description?: string;
      type?: 'exterior' | 'interior' | 'damage' | 'document';
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(`${this.basePath}/${id}/images`, imageData);
  }

  async addVehicleDocument(
    id: string, 
    documentData: {
      name: string;
      url: string;
      type?: 'registration' | 'insurance' | 'inspection' | 'manual' | 'other';
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(`${this.basePath}/${id}/documents`, documentData);
  }

  async updateMileage(id: string, mileage: number): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(`${this.basePath}/${id}/mileage`, { mileage });
  }

  async getFleetSummary(): Promise<ApiResponse<FleetSummary>> {
    console.log('🚗 VehicleService: getFleetSummary called');
    const endpoint = `${this.basePath}/admin/fleet-summary`;
    console.log('🔗 VehicleService: Calling endpoint:', endpoint);
    
    try {
      const response = await apiService.get<FleetSummary>(endpoint);
      console.log('✅ VehicleService: getFleetSummary response:', response);
      return response;
    } catch (error) {
      console.error('❌ VehicleService: getFleetSummary error:', error);
      throw error;
    }
  }

  async getMaintenanceSchedule(days: number = 30): Promise<ApiResponse<Vehicle[]>> {
    return apiService.get<Vehicle[]>(`${this.basePath}/admin/maintenance-schedule?days=${days}`);
  }

  async getVehiclesByLocation(): Promise<ApiResponse<{ [location: string]: Vehicle[] }>> {
    return apiService.get<{ [location: string]: Vehicle[] }>(`${this.basePath}/admin/by-location`);
  }

  async getVehiclesAvailability(): Promise<ApiResponse<VehicleAvailability[]>> {
    return apiService.get<VehicleAvailability[]>(`${this.basePath}/admin/availability`);
  }

  async getFleetDashboardData(): Promise<ApiResponse<{
    vehicles: Vehicle[];
    summary: FleetSummary;
    availability: VehicleAvailability[];
  }>> {
    return apiService.get(`${this.basePath}/admin/dashboard-data`);
  }

  /**
   * Force a vehicle to be available by completing only current/past bookings.
   * Future bookings are preserved. Use this when a car shows "Sold Out" incorrectly.
   */
  async forceVehicleAvailable(vehicleId: string): Promise<ApiResponse<any>> {
    return apiService.post<any>(`/api/bookings/admin/force-complete-car/${vehicleId}`, {});
  }

  /**
   * Simply set vehicle status to 'available' without touching any bookings.
   * Use this to fix status issues (out-of-service, maintenance) without
   * affecting any customer reservations.
   */
  async setVehicleAvailable(vehicleId: string): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(`${this.basePath}/${vehicleId}/status`, {
      status: 'available',
      notes: 'Status reset to available by admin',
    });
  }

  /**
   * Uploads a vehicle photo and returns its stored URL.
   * The file is saved by the API under /uploads/car-images/.
   */
  async uploadVehicleImage(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    // postForm, not post: post() JSON-stringifies its body, which would
    // destroy the file upload.
    return apiService.postForm<{ url: string }>('/api/admin/car-images/upload', formData);
  }

  async deleteVehicle(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`${this.basePath}/${id}`);
  }

  // --- MAINTENANCE BLOCKS ---

  async addMaintenanceBlock(
    vehicleId: string,
    blockData: {
      startDate: string;
      endDate: string;
      reason?: string;
      createdBy?: string;
      unitsBlocked?: number;
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(
      `${this.basePath}/${vehicleId}/maintenance-blocks`,
      blockData,
    );
  }

  async getMaintenanceBlocks(vehicleId: string): Promise<ApiResponse<any[]>> {
    return apiService.get<any[]>(`${this.basePath}/${vehicleId}/maintenance-blocks`);
  }

  async cancelMaintenanceBlock(vehicleId: string, blockIndex: number): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${vehicleId}/maintenance-blocks/${blockIndex}/cancel`,
      {},
    );
  }

  async completeMaintenanceBlock(vehicleId: string, blockIndex: number): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${vehicleId}/maintenance-blocks/${blockIndex}/complete`,
      {},
    );
  }

  // --- ADMIN DATE BLOCKS (Corporate, Events, Seasonal) ---

  async addDateBlock(
    vehicleId: string,
    blockData: {
      startDate: string;
      endDate: string;
      reason?: string;
      clientName?: string;
      createdBy?: string;
      unitsBlocked?: number;
      blockType?: 'corporate' | 'event' | 'seasonal' | 'internal' | 'other';
    }
  ): Promise<ApiResponse<Vehicle>> {
    return apiService.post<Vehicle>(
      `${this.basePath}/${vehicleId}/date-blocks`,
      blockData,
    );
  }

  async getDateBlocks(vehicleId: string): Promise<ApiResponse<any[]>> {
    return apiService.get<any[]>(`${this.basePath}/${vehicleId}/date-blocks`);
  }

  async cancelDateBlock(vehicleId: string, blockIndex: number): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${vehicleId}/date-blocks/${blockIndex}/cancel`,
      {},
    );
  }

  async completeDateBlock(vehicleId: string, blockIndex: number): Promise<ApiResponse<Vehicle>> {
    return apiService.patch<Vehicle>(
      `${this.basePath}/${vehicleId}/date-blocks/${blockIndex}/complete`,
      {},
    );
  }
}

export const vehicleService = new VehicleService();
